import { z } from "zod";
import { ApiTokenScope, DEFAULT_API_TOKEN_SCOPES } from "./mcp.ts";
import { ProviderId, ProviderSelection, ReasoningEffort } from "./providers.ts";
import {
  AttachmentRef,
  AttachmentRefs,
  Chat,
  ChatEvent,
  ChatEventsCoverage,
  FileChange,
  GitCommit,
  GitHubInstallation,
  Invite,
  Job,
  Org,
  OrgMember,
  Repo,
  RepoAuth,
  RepoIcon,
  Snapshot,
  TaskApproval,
  User,
  WorkerAccessGrant,
  WorkerInfo,
  Worktree,
  PullRequestInfo,
  PullRequestMergeMethod,
  TEAM_MESSAGE_MAX_CHARS,
  TeamMentions,
  TeamMessageContext,
  InboxNotification,
} from "./models.ts";

/**
 * REST API contract. Auth endpoints live under `/api/auth/*` and are owned by
 * better-auth (email+password, TOTP 2FA, bearer sessions) — not modeled here.
 * Everything else is `/api/...`, JSON in/out, zod-validated server-side.
 */

// -- billing -----------------------------------------------------------------

/** POST /api/billing/checkout — public; starts a Stripe Checkout for a license. */
export const CheckoutRequest = z.object({
  /** Total seats. 2 are included in the base price; extras are discounted. */
  seats: z.number().int().min(2).max(100).default(2),
  /** Native checkout returns through the Apple universal-link claim path. */
  client: z.enum(["native"]).optional(),
});
export type CheckoutRequest = z.infer<typeof CheckoutRequest>;
export const CheckoutResponse = z.object({ url: z.string() });
export type CheckoutResponse = z.infer<typeof CheckoutResponse>;

/** GET /api/billing/claim?session_id= — public; verifies a paid session. */
export const ClaimResponse = z.object({
  claimToken: z.string(),
  seats: z.number(),
  purchaseEmail: z.string(),
});
export type ClaimResponse = z.infer<typeof ClaimResponse>;

// -- me / orgs ---------------------------------------------------------------

/** One signed-in native device. Revoking it (`DELETE /api/native/devices/:id`)
 * ends the session it is bound to, so a listed device is always live. */
export const NativeDeviceInfo = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  enrolledAt: z.number().int().nonnegative(),
  lastSeenAt: z.number().int().nonnegative(),
  current: z.boolean(),
});
export type NativeDeviceInfo = z.infer<typeof NativeDeviceInfo>;
export const NativeDevicesResponse = z.object({ devices: z.array(NativeDeviceInfo) });
export type NativeDevicesResponse = z.infer<typeof NativeDevicesResponse>;

/** GET /api/me/sessions — one of the account's sign-in sessions. Never
 * carries the session token: a session is named and revoked by `id` alone
 * (`DELETE /api/me/sessions/:id`). */
export const AuthSessionInfo = z.object({
  id: z.string().min(1),
  createdAt: z.number().int().nonnegative(),
  lastActiveAt: z.number().int().nonnegative(),
  expiresAt: z.number().int().nonnegative(),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  /** The native device the session is bound to; null for a browser session. */
  nativeDeviceId: z.string().nullable(),
  current: z.boolean(),
});
export type AuthSessionInfo = z.infer<typeof AuthSessionInfo>;
export const AuthSessionsResponse = z.object({ sessions: z.array(AuthSessionInfo) });
export type AuthSessionsResponse = z.infer<typeof AuthSessionsResponse>;
/** POST /api/me/sessions/revoke-others — every session but the caller's own
 * is ended. */
export const RevokeOtherSessionsResponse = z.object({ revoked: z.number().int().nonnegative() });
export type RevokeOtherSessionsResponse = z.infer<typeof RevokeOtherSessionsResponse>;

/**
 * Which chord submits a prompt: the composer's send, and the create-task form.
 * `enter` — the default — sends on Return and leaves Shift-Return for a
 * newline; `modEnter` sends only on Command-Return (Control-Return on
 * platforms without Command) and gives Return back to the text.
 *
 * Command-Return submits under both settings. It was the only chord Rivets ever
 * had, so taking it away would break the muscle memory of everyone who never
 * visits this setting.
 */
export const SubmitShortcut = z.enum(["enter", "modEnter"]);
export type SubmitShortcut = z.infer<typeof SubmitShortcut>;
export const DEFAULT_SUBMIT_SHORTCUT: SubmitShortcut = "enter";

/**
 * What sending a message does while the agent is still working. `steer` — the
 * default — hands it to the agent straight away, interrupting the turn in
 * progress exactly like the queue's "send now". `queue` holds it until the
 * current turn finishes, where its author can still edit, delete or send it now.
 */
export const WhileWorkingBehavior = z.enum(["steer", "queue"]);
export type WhileWorkingBehavior = z.infer<typeof WhileWorkingBehavior>;
export const DEFAULT_WHILE_WORKING: WhileWorkingBehavior = "steer";

/**
 * Account-level agent defaults. Stored server-side so every signed-in device —
 * web, Mac, iPhone — preselects the same agent, model and effort. A `null`
 * field means "no preference": clients fall back to whatever the routed worker
 * reports as available, exactly as they did before a default was ever set.
 * Defaults are advisory, never authoritative: a default that a worker no longer
 * offers is simply not applied, so preferences can never block task creation.
 *
 * `submitShortcut` is the one field here that is not advisory and never null:
 * a keystroke has to mean something, so an account that has never chosen reads
 * as the default rather than as "unset". `whileWorking` is the same: a send
 * always has to either steer or queue.
 *
 * `showTurnCost` decides whether the latest turn's token and dollar caption is
 * drawn under the agent's answer. Off unless the account turns it on: most
 * people run agents on a subscription, where a per-turn price means nothing.
 */
export const UserPreferences = z.object({
  defaultProviderId: ProviderId.nullable(),
  defaultModel: z.string().trim().min(1).max(160).nullable(),
  defaultReasoningEffort: ReasoningEffort.nullable(),
  submitShortcut: SubmitShortcut,
  whileWorking: WhileWorkingBehavior,
  showTurnCost: z.boolean(),
  updatedAt: z.number().int().nonnegative(),
});
export type UserPreferences = z.infer<typeof UserPreferences>;

export const EMPTY_USER_PREFERENCES: UserPreferences = {
  defaultProviderId: null,
  defaultModel: null,
  defaultReasoningEffort: null,
  submitShortcut: DEFAULT_SUBMIT_SHORTCUT,
  whileWorking: DEFAULT_WHILE_WORKING,
  showTurnCost: false,
  updatedAt: 0,
};

/** GET /api/me/preferences */
export const UserPreferencesResponse = z.object({ preferences: UserPreferences });
export type UserPreferencesResponse = z.infer<typeof UserPreferencesResponse>;

/** PUT /api/me/preferences — a full replacement of the agent defaults; omit
 * nothing, `null` clears.
 *
 * `submitShortcut`, `whileWorking` and `showTurnCost` are the exception: each
 * has its own settings control that saves it alone, so absent or null means
 * "keep what is stored" rather than "reset". */
export const UpdateUserPreferencesRequest = UserPreferences
  .omit({ updatedAt: true, submitShortcut: true, whileWorking: true, showTurnCost: true })
  .extend({ submitShortcut: SubmitShortcut.nullish(), whileWorking: WhileWorkingBehavior.nullish(), showTurnCost: z.boolean().nullish() });
export type UpdateUserPreferencesRequest = z.infer<typeof UpdateUserPreferencesRequest>;

export const KeyboardShortcutCommand = z.enum([
  "closeChatTab", "commandPalette", "copyConciseTranscript", "findInTranscript", "focusPromptComposer", "goBack", "goForward", "newChat", "newTask", "nextChat",
  "previousChat", "showKeyboardShortcuts", "toggleAssistant", "toggleInspector", "toggleSidebar",
  // Settings, cancel-run and open-in-editor joined the desktop catalog so they
  // can be rebound like every other command; they sync through the same
  // account row. `openInEditor` and `newWindow` only have handlers on the
  // desktop (and iPad, for `newWindow`).
  "openSettings", "cancelRun", "openInEditor", "newWindow", "selectTask1", "selectTask2",
  "selectTask3", "selectTask4", "selectTask5", "selectTask6", "selectTask7",
  "selectTask8", "selectTask9",
]);
export type KeyboardShortcutCommand = z.infer<typeof KeyboardShortcutCommand>;
export const KeyboardShortcutModifier = z.enum(["command", "option", "shift", "control"]);
export type KeyboardShortcutModifier = z.infer<typeof KeyboardShortcutModifier>;
export const KeyboardShortcutBinding = z.object({
  key: z.string().length(1),
  modifiers: z.array(KeyboardShortcutModifier).min(1).max(4),
}).superRefine((binding, context) => {
  if (new Set(binding.modifiers).size !== binding.modifiers.length) {
    context.addIssue({ code: "custom", message: "Shortcut modifiers must be unique" });
  }
  if (!binding.modifiers.some((modifier) => modifier !== "shift")) {
    context.addIssue({ code: "custom", message: "Shortcut requires Command, Control, or Option" });
  }
});
export type KeyboardShortcutBinding = z.infer<typeof KeyboardShortcutBinding>;
export const KeyboardShortcutBindings = z.partialRecord(KeyboardShortcutCommand, KeyboardShortcutBinding);
export type KeyboardShortcutBindings = z.infer<typeof KeyboardShortcutBindings>;
export const KeyboardShortcutPreferences = z.object({
  bindings: KeyboardShortcutBindings,
  updatedAt: z.number().int().nonnegative(),
});
export type KeyboardShortcutPreferences = z.infer<typeof KeyboardShortcutPreferences>;
export const EMPTY_KEYBOARD_SHORTCUT_PREFERENCES: KeyboardShortcutPreferences = { bindings: {}, updatedAt: 0 };
export const KeyboardShortcutPreferencesResponse = z.object({ keyboardShortcuts: KeyboardShortcutPreferences });
export type KeyboardShortcutPreferencesResponse = z.infer<typeof KeyboardShortcutPreferencesResponse>;
export const UpdateKeyboardShortcutPreferencesRequest = z.object({ bindings: KeyboardShortcutBindings });
export type UpdateKeyboardShortcutPreferencesRequest = z.infer<typeof UpdateKeyboardShortcutPreferencesRequest>;

export const MeResponse = z.object({
  /** `isSuperAdmin` unlocks the web app's /admin console. The console's own API
   * (`/api/admin/*`) re-checks it on every request — this field is only what the
   * client uses to decide whether to show the entry point. */
  user: User.extend({
    isSuperAdmin: z.boolean(),
    /** Whether the account confirmed its address through the verification
     * email. Shown with a resend action in account settings; sign-in does
     * not depend on it. */
    emailVerified: z.boolean(),
  }),
  orgs: z.array(Org.extend({ role: z.enum(["owner", "member"]) })),
  /** Carried on the bootstrap response so no client needs a second round trip
   * before it can preselect the account's agent defaults. */
  preferences: UserPreferences,
  /** Per-command overrides shared by every device signed in as this user. */
  keyboardShortcuts: KeyboardShortcutPreferences,
});
export type MeResponse = z.infer<typeof MeResponse>;

/** POST /api/orgs — requires auth + an unclaimed license claim token. */
export const CreateOrgRequest = z.object({
  name: z.string().min(1).max(80),
  claimToken: z.string(),
});
export type CreateOrgRequest = z.infer<typeof CreateOrgRequest>;

export const OrgDetailResponse = z.object({
  org: Org,
  members: z.array(OrgMember),
  invites: z.array(Invite),
  role: z.enum(["owner", "member"]),
});
export type OrgDetailResponse = z.infer<typeof OrgDetailResponse>;

/** Small, intentionally stable overview used by every home client. */
export const HomeStatsResponse = z.object({
  activeTasks: z.number().int().nonnegative(),
  mergedPullRequests: z.number().int().nonnegative(),
  tokensLast7Days: z.number().int().nonnegative(),
  tasksLast7Days: z.number().int().nonnegative(),
});
export type HomeStatsResponse = z.infer<typeof HomeStatsResponse>;

/** Organization-wide settings. Owners replace every value at once. */
export const UpdateOrganizationSettingsRequest = z.object({
  mergeMethod: PullRequestMergeMethod,
  autoArchiveMergedPullRequests: z.boolean(),
  requireIntegrationApproval: z.boolean(),
});
export type UpdateOrganizationSettingsRequest = z.infer<typeof UpdateOrganizationSettingsRequest>;

/** A null repository value restores inheritance from the organization. */
export const UpdateRepositorySettingsRequest = z.object({
  mergeMethod: PullRequestMergeMethod.nullable(),
});
export type UpdateRepositorySettingsRequest = z.infer<typeof UpdateRepositorySettingsRequest>;

/** POST /api/orgs/:orgId/invites */
export const CreateInviteRequest = z.object({ email: z.string().email() });
export type CreateInviteRequest = z.infer<typeof CreateInviteRequest>;
/** Response includes the one-time invite URL token (shown to the inviter to share).
 * `emailSent` reports whether the invite email was delivered to the provider —
 * the link remains the fallback when email is unconfigured or fails. */
export const CreateInviteResponse = z.object({
  invite: Invite,
  token: z.string(),
  emailSent: z.boolean().optional(),
});
export type CreateInviteResponse = z.infer<typeof CreateInviteResponse>;

/** POST /api/invites/accept — requires auth. */
export const AcceptInviteRequest = z.object({ token: z.string() });
export type AcceptInviteRequest = z.infer<typeof AcceptInviteRequest>;

// -- repos -------------------------------------------------------------------

/** POST /api/orgs/:orgId/repos — exactly one of `githubRepoId` (App-connected,
 * preferred) or `gitUrl` (credential-free URL: public https or file://). */
export const CreateRepoRequest = z
  .object({
    name: z.string().min(1).max(100),
    defaultBranch: z.string().default("main"),
    gitUrl: z.string().min(1).optional(),
    githubRepoId: z.number().int().optional(),
    /** Client-generated unique id; replaying the same id returns the original result instead of creating a duplicate. */
    clientRequestId: z.string().min(8).max(64).optional(),
  })
  .refine((d) => Boolean(d.gitUrl) !== Boolean(d.githubRepoId), {
    message: "Provide either gitUrl or githubRepoId, not both",
  });
export type CreateRepoRequest = z.infer<typeof CreateRepoRequest>;

/** PUT /api/orgs/:orgId/repos/order — the caller's own left-sidebar order for
 * this organization's projects. Ordering is per user, not per organization: a
 * teammate dragging their sidebar must not move anyone else's.
 *
 * The list is a preference, not an assertion about membership — ids that do not
 * belong to the organization are dropped, and repositories the list omits (a
 * project connected on another device since) keep following the stored order
 * and then the connection order. */
export const ReorderReposRequest = z.object({
  repoIds: z.array(z.string().min(1)).max(500),
});
export type ReorderReposRequest = z.infer<typeof ReorderReposRequest>;

// -- github app --------------------------------------------------------------

/** GET /api/orgs/:orgId/github — connection status + install URL for owners. */
export const GitHubStatusResponse = z.object({
  installation: GitHubInstallation.nullable(),
  installUrl: z.string(),
});
export type GitHubStatusResponse = z.infer<typeof GitHubStatusResponse>;

/** GET /api/orgs/:orgId/github/repos — repos the installation can access. */
export const GitHubRepoOption = z.object({
  githubRepoId: z.number(),
  fullName: z.string(),
  private: z.boolean(),
  defaultBranch: z.string(),
  /** The owner's public GitHub avatar. Pickers show it immediately so a row
   * never renders as a bare monogram while the repository's own icon is still
   * being resolved in the background. */
  ownerAvatarUrl: z.string().nullish(),
  /** Content address of the repository's own icon, once the API has resolved
   * one. Absent on the first listing of a repository; the API broadcasts
   * `github.updated` when a round of resolution finishes, and the next listing
   * carries it. */
  icon: RepoIcon.nullish(),
});
export type GitHubRepoOption = z.infer<typeof GitHubRepoOption>;
export const GitHubReposResponse = z.object({ repos: z.array(GitHubRepoOption) });
export type GitHubReposResponse = z.infer<typeof GitHubReposResponse>;

// -- external integrations --------------------------------------------------

/** Runtime defaults used when an external source asks Rivets to start work.
 * Provider-specific routing rules can override this server-side.
 *
 * `provider` is an *override*, and absent by default: an issue delegated in
 * Linear starts the same kind of run a person starts in the composer, so with
 * nothing set here it runs on the agent defaults of whoever delegated it. Only
 * an integration that has deliberately pinned an agent carries one. */
export const IntegrationTaskDefaults = z.object({
  repoId: z.string().min(1).optional(),
  baseBranch: z.string().min(1).optional(),
  provider: ProviderSelection.optional(),
  routing: z.discriminatedUnion("mode", [
    z.object({ mode: z.literal("auto"), preferredWorkerId: z.string().min(1).optional() }).strict(),
    z.object({ mode: z.literal("cluster"), clusterId: z.string().min(1) }).strict(),
  ]).optional().default({ mode: "auto" }),
});
export type IntegrationTaskDefaults = z.infer<typeof IntegrationTaskDefaults>;

export const IntegrationConnection = z.object({
  id: z.string(),
  provider: z.string(),
  status: z.enum(["active", "revoked", "error"]),
  externalTenantId: z.string(),
  externalTenantName: z.string().nullish(),
  externalTenantUrl: z.string().url().nullish(),
  scopes: z.array(z.string()),
  defaults: IntegrationTaskDefaults,
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type IntegrationConnection = z.infer<typeof IntegrationConnection>;

export const IntegrationStatusResponse = z.object({
  configured: z.boolean(),
  connection: IntegrationConnection.nullable(),
  connectUrl: z.string().optional(),
});
export type IntegrationStatusResponse = z.infer<typeof IntegrationStatusResponse>;

export const UpdateIntegrationRequest = z.object({
  defaults: IntegrationTaskDefaults,
});
export type UpdateIntegrationRequest = z.infer<typeof UpdateIntegrationRequest>;

// -- account links -----------------------------------------------------------

/** Who can be linked: the providers whose senders get a one-time challenge. */
export const AccountLinkProvider = z.enum(["slack", "teams", "linear"]);
export type AccountLinkProvider = z.infer<typeof AccountLinkProvider>;

/** A challenge token: 32 random bytes, hex. Only its SHA-256 is stored. */
const AccountLinkToken = z.string().regex(/^[0-9a-f]{64}$/i);

/**
 * GET /api/account-links/:token — what confirming would bind, read without
 * consuming the challenge. Clients never redeem on load: they show this and
 * wait for an explicit Confirm (security review 01 H1, 03 H-4, 04 H2).
 */
export const AccountLinkPreview = z.object({
  provider: AccountLinkProvider,
  /** The Slack / Teams / Linear user the challenge was issued to. */
  external: z.object({
    userId: z.string(),
    name: z.string().nullable(),
    email: z.string().nullable(),
    /** The provider itself vouches for `email` (Slack `is_email_confirmed`). */
    emailVerified: z.boolean(),
  }),
  /** The provider workspace the sender wrote from. */
  workspace: z.object({ id: z.string(), name: z.string().nullable() }),
  organization: z.object({ id: z.string(), name: z.string() }),
  /** The signed-in Rivets account that would become this sender. */
  account: z.object({ name: z.string(), email: z.string(), emailVerified: z.boolean() }),
  /**
   * `match`: both emails verified and equal. `mismatch`: both verified and
   * different — clients warn prominently and label the button "Link anyway".
   * `unverified`: one side is unknown or unverified, so nothing can be said.
   */
  emailCheck: z.enum(["match", "mismatch", "unverified"]),
  expiresAt: z.number(),
});
export type AccountLinkPreview = z.infer<typeof AccountLinkPreview>;
export const AccountLinkPreviewResponse = z.object({ preview: AccountLinkPreview });
export type AccountLinkPreviewResponse = z.infer<typeof AccountLinkPreviewResponse>;

/**
 * POST /api/account-links — the explicit confirmation. Linking never runs the
 * message that produced the challenge: the sender is told they are linked and
 * asked to send it again.
 */
export const RedeemAccountLinkRequest = z.object({ token: AccountLinkToken });
export type RedeemAccountLinkRequest = z.infer<typeof RedeemAccountLinkRequest>;
export const RedeemAccountLinkResponse = z.object({
  linked: z.literal(true),
  provider: AccountLinkProvider,
  organizationId: z.string(),
});
export type RedeemAccountLinkResponse = z.infer<typeof RedeemAccountLinkResponse>;

// -- native OAuth completion -------------------------------------------------

/**
 * A native app proves it started an OAuth/install flow PKCE-style: it sends
 * `binding` = base64url(SHA-256(verifier)) when it asks for the URL, the
 * provider callback parks the result and returns to the app with a one-time
 * `flow` (`/{provider}/connected?{provider}=verify&orgId=…&flow=…`), and only
 * the same signed-in user holding the verifier can finish it here.
 */
export const OAuthBindingChallenge = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
export const CompleteNativeOAuthRequest = z.object({
  flow: z.string().regex(/^[0-9a-f]{64}$/i),
  verifier: z.string().regex(/^[A-Za-z0-9_-]{43,128}$/),
});
export type CompleteNativeOAuthRequest = z.infer<typeof CompleteNativeOAuthRequest>;
export const CompleteNativeOAuthResponse = z.object({
  provider: z.enum(["github", "linear", "slack", "teams"]),
  orgId: z.string(),
  /** The same result strings the web callback puts in `?{provider}=`. */
  result: z.string(),
});
export type CompleteNativeOAuthResponse = z.infer<typeof CompleteNativeOAuthResponse>;

/** GET /api/worker/repos/:repoId/git-credentials (worker bearer token).
 * `cloneUrl` is ALWAYS the plain URL (no embedded secrets — it may reach
 * argv/.git/config). For github-app repos the short-lived token comes as
 * separate `username`/`password` fields, to be supplied to git exclusively
 * via a credential-helper/askpass channel, held in memory only, and
 * refetched after `expiresAt`. */
export const GitCredentialsResponse = z.object({
  cloneUrl: z.string(),
  auth: RepoAuth,
  username: z.string().optional(),
  password: z.string().optional(),
  expiresAt: z.number().nullish(),
});
export type GitCredentialsResponse = z.infer<typeof GitCredentialsResponse>;

// -- workers -----------------------------------------------------------------

/** POST /api/orgs/:orgId/workers — registers a worker; token shown once. */
export const RegisterWorkerRequest = z.object({
  name: z.string().min(1).max(60),
  /** Join an existing cluster. Omit to create a cluster for this worker. */
  clusterId: z.string().min(1).optional(),
  /** Name for a newly-created cluster; defaults to the worker name. */
  clusterName: z.string().min(1).max(60).optional(),
  /** Client-generated unique id; replaying the same id returns the original result instead of creating a duplicate. */
  clientRequestId: z.string().min(8).max(64).optional(),
});
export type RegisterWorkerRequest = z.infer<typeof RegisterWorkerRequest>;
export const RegisterWorkerResponse = z.object({
  worker: WorkerInfo,
  /** Plaintext bearer token for the CLI — stored hashed server-side. */
  token: z.string(),
});
export type RegisterWorkerResponse = z.infer<typeof RegisterWorkerResponse>;

/** A team as a worker owner sees it when choosing who may use the worker:
 * its name, not its members or repositories. */
export const WorkerAccessTeam = z.object({ id: z.string(), name: z.string() });
export type WorkerAccessTeam = z.infer<typeof WorkerAccessTeam>;

/** GET /api/orgs/:orgId/workers — every active worker, with `canUse` computed
 * for the caller, and the org's teams for the owner's access editor. */
export const WorkersResponse = z.object({
  workers: z.array(WorkerInfo),
  teams: z.array(WorkerAccessTeam),
});
export type WorkersResponse = z.infer<typeof WorkersResponse>;

/** PUT /api/orgs/:orgId/workers/:workerId/access — owner only. Replaces the
 * whole grant list; an empty list makes the worker owner-only. */
export const UpdateWorkerAccessRequest = z.object({
  access: z.array(WorkerAccessGrant).max(200),
});
export type UpdateWorkerAccessRequest = z.infer<typeof UpdateWorkerAccessRequest>;

/** PUT /api/orgs/:orgId/workers/:workerId/owner — owner only. The new owner
 * must be a member; the grant list is kept as it is. */
export const TransferWorkerRequest = z.object({
  ownerUserId: z.string().min(1).max(100),
});
export type TransferWorkerRequest = z.infer<typeof TransferWorkerRequest>;

export const WorkerResponse = z.object({ worker: WorkerInfo });
export type WorkerResponse = z.infer<typeof WorkerResponse>;

// -- worktrees ---------------------------------------------------------------

export const WORKTREE_ARCHIVE_RETENTION_MS = 7 * 24 * 60 * 60 * 1_000;

/** GET /api/repos/:repoId/branches */
export const RepoBranchesResponse = z.object({
  branches: z.array(z.string().min(1)),
  defaultBranch: z.string().min(1),
});
export type RepoBranchesResponse = z.infer<typeof RepoBranchesResponse>;

/** An open pull request a task can be started on. Fork heads are listed but
 * cannot be checked out and pushed with the repository's credentials, so
 * clients show them disabled. */
export const RepoPullRequestOption = z.object({
  number: z.number().int().positive(),
  title: z.string(),
  url: z.string(),
  headBranch: z.string().min(1),
  baseBranch: z.string().min(1),
  author: z.string().nullable(),
  draft: z.boolean(),
  fromFork: z.boolean(),
  updatedAt: z.number(),
});
export type RepoPullRequestOption = z.infer<typeof RepoPullRequestOption>;

/** GET /api/repos/:repoId/pull-requests — open pull requests, most recently updated first. */
export const RepoPullRequestsResponse = z.object({
  pullRequests: z.array(RepoPullRequestOption),
});
export type RepoPullRequestsResponse = z.infer<typeof RepoPullRequestsResponse>;

/** A Linear issue a task can be started from, with enough workflow state to
 * draw the status glyph Linear itself uses. */
export const LinearIssueOption = z.object({
  id: z.string(),
  identifier: z.string(),
  title: z.string(),
  url: z.string(),
  state: z.object({
    name: z.string(),
    /** triage, backlog, unstarted, started, completed or canceled. */
    type: z.string(),
    color: z.string().nullable(),
  }).nullable(),
  /** 0 none, 1 urgent, 2 high, 3 medium, 4 low. */
  priority: z.number().int().min(0).max(4),
  assignee: z.string().nullable(),
  updatedAt: z.number(),
});
export type LinearIssueOption = z.infer<typeof LinearIssueOption>;

/** GET /api/orgs/:orgId/linear/issues?query= — open issues, most recently
 * updated first. `connected` is false (and `issues` empty) when the
 * organization has no active Linear connection, so clients can say so instead
 * of showing an error. */
export const LinearIssuesResponse = z.object({
  connected: z.boolean(),
  issues: z.array(LinearIssueOption),
});
export type LinearIssuesResponse = z.infer<typeof LinearIssuesResponse>;

/** What a new task starts from, beyond the repository's default branch.
 * - `branch`: work directly on an existing remote branch.
 * - `pullRequest`: work directly on an open pull request's head branch,
 *   diffed against its base.
 * - `linearIssue`: a new branch named for the issue, with the issue's content
 *   given to the agent; the prompt may be left empty. */
export const TaskStart = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("branch"), branch: z.string().min(1).max(255) }).strict(),
  z.object({ kind: z.literal("pullRequest"), number: z.number().int().positive() }).strict(),
  z.object({ kind: z.literal("linearIssue"), issueId: z.string().min(1).max(64) }).strict(),
]);
export type TaskStart = z.infer<typeof TaskStart>;

/** Where a new task should execute. A cluster can contain multiple worker
 * daemons. Auto routing may prefer a caller-local worker, but always falls
 * back when it is unavailable or incompatible. Only workers the caller may use
 * (`WorkerInfo.canUse`) are ever considered: preferring or pinning one they
 * may not use is refused (`worker_access_denied`), never silently honoured. */
export const TaskRouting = z.discriminatedUnion("mode", [
  z.object({
    mode: z.literal("auto"),
    preferredWorkerId: z.string().min(1).optional(),
  }).strict(),
  z.object({
    mode: z.literal("cluster"),
    clusterId: z.string().min(1),
  }).strict(),
]);
export type TaskRouting = z.infer<typeof TaskRouting>;

/** POST /api/repos/:repoId/tasks — creates the worktree, first chat, and job. */
export const CreateTaskRequest = z.object({
  provider: ProviderSelection,
  /** May be empty only when starting from a Linear issue, whose content becomes the prompt. */
  prompt: z.string().trim().max(20_000),
  baseBranch: z.string().min(1).optional(),
  /** Omitted for a fresh branch off `baseBranch` (or the repository default). */
  start: TaskStart.optional(),
  /** Omitted means unpinned Auto routing. */
  routing: TaskRouting.optional().default({ mode: "auto" }),
  /** Images uploaded while composing the task. */
  attachments: AttachmentRefs.optional(),
  /** Client-generated unique id; replaying the same id returns the original result instead of creating a duplicate. */
  clientRequestId: z.string().min(8).max(64),
}).refine((request) => request.prompt.length > 0 || request.start?.kind === "linearIssue", {
  message: "A prompt is required",
  path: ["prompt"],
});
export type CreateTaskRequest = z.infer<typeof CreateTaskRequest>;
export type CreateTaskRequestInput = z.input<typeof CreateTaskRequest>;

export const CreateTaskResponse = z.object({
  worktree: Worktree,
  chat: Chat,
  job: Job,
});
export type CreateTaskResponse = z.infer<typeof CreateTaskResponse>;

/** GET /api/chats/:id/approval — the chat's approval, pending or decided, with
 * the viewer's own `canApprove` / `canReject`. 404 when the chat never needed
 * one. */
export const TaskApprovalResponse = z.object({ approval: TaskApproval });
export type TaskApprovalResponse = z.infer<typeof TaskApprovalResponse>;

/** POST /api/chats/:id/approval/approve and …/reject. Approving dispatches the
 * first run exactly once, however many members press it; a decision that has
 * already been made is a 409 `approval_decided`. */
export const DecideTaskApprovalRequest = z.object({
  /** Client-generated unique id; replaying the same id returns the original result instead of deciding twice. */
  clientRequestId: z.string().min(8).max(64),
});
export type DecideTaskApprovalRequest = z.infer<typeof DecideTaskApprovalRequest>;

export const WorktreeDetailResponse = z.object({
  worktree: Worktree,
  participantIds: z.array(z.string()).optional(),
  chats: z.array(Chat),
  latestSnapshotId: z.string().nullish(),
  pullRequest: PullRequestInfo.nullable(),
});
export type WorktreeDetailResponse = z.infer<typeof WorktreeDetailResponse>;

/** POST /api/worktrees/:id/archive and /restore. */
export const WorktreeMutationResponse = z.object({ worktree: Worktree });
export type WorktreeMutationResponse = z.infer<typeof WorktreeMutationResponse>;

/** Worker cleanup is claim-based so restore cannot race local deletion. */
export const WorktreePurgeCandidate = z.object({
  id: z.string(),
  repoId: z.string(),
  archivedAt: z.number(),
});
export type WorktreePurgeCandidate = z.infer<typeof WorktreePurgeCandidate>;
export const WorktreePurgeClaimResponse = WorktreePurgeCandidate.extend({ claimToken: z.string().min(1) });
export type WorktreePurgeClaimResponse = z.infer<typeof WorktreePurgeClaimResponse>;

/** One immediate child of a directory in the worktree. A symlink is reported
 * as a file whatever it points at: the browser must not follow one out of the
 * worktree, and a link is previewable as its target text. */
export const DirectoryEntry = z.object({
  name: z.string().min(1).max(500),
  directory: z.boolean(),
});
export type DirectoryEntry = z.infer<typeof DirectoryEntry>;

/** Ceiling on one directory response. Generated directories can hold tens of
 * thousands of siblings, and no tree is readable at that size; the browser
 * says so rather than pretending the rest is not there. */
export const MAX_DIRECTORY_ENTRIES = 2_000;

/** GET /api/worktrees/:id/dir?path=… — one directory's children, read live
 * from the worker's checkout. Unlike the snapshot path list this is never
 * filtered by git: ignored files and directories are part of the worktree and
 * the browser shows them. */
export const DirectoryListingResponse = z.object({
  path: z.string(),
  entries: z.array(DirectoryEntry),
  truncated: z.boolean(),
  error: z.string().nullish(),
});
export type DirectoryListingResponse = z.infer<typeof DirectoryListingResponse>;

/** GET /api/worktrees/:id/file?path=… — on-demand file fetch through the worker.
 * A previewable image is `binary` with its `mediaType` set, and then `content`
 * is its bytes in base64. */
export const FileContentResponse = z.object({
  path: z.string(),
  content: z.string().nullish(),
  binary: z.boolean(),
  mediaType: z.string().nullish(),
  error: z.string().nullish(),
});
export type FileContentResponse = z.infer<typeof FileContentResponse>;

/** The worker's `fetchFile` errors that mean the path names no file it can
 * serve, as against a file it could not read just now. */
const FILE_ERRORS_NAMING_NOTHING: ReadonlySet<string> = new Set([
  "file not found",
  "not a regular file",
  "path escapes the worktree",
  "absolute paths are not allowed",
]);

/** Whether the worker's answer for an image path says there is no image
 * there: no such file in the worktree, or a file that is not an image. An
 * agent mentions files it did not make — a logo outside the worktree, a file
 * on another branch — and those are not drawn as its attachments. A read that
 * failed on the way (an offline worker, a timeout) throws instead, and says
 * nothing. Swift twin: `AppModel.imageRead`. */
export function fileResponseNamesNoImage(response: FileContentResponse): boolean {
  if (response.binary && response.mediaType && response.content) return false;
  return response.error == null || FILE_ERRORS_NAMING_NOTHING.has(response.error);
}

/** PUT /api/worktrees/:id/file — saves an edited text file in the worker's
 * checkout. Only an existing file of at most `MAX_FILE_PREVIEW_BYTES` is
 * written. `expectedHash` is `fileContentHash` of the text the edit began
 * from: when the file on disk no longer matches it the save is refused with
 * 409 `file_conflict` and nothing is written. Omit it to overwrite. */
export const WriteFileRequest = z.object({
  path: z.string().min(1).max(4_096),
  content: z.string(),
  expectedHash: z.string().regex(/^[0-9a-f]{64}$/).nullish(),
});
export type WriteFileRequest = z.infer<typeof WriteFileRequest>;
export const WriteFileResponse = z.object({
  path: z.string(),
  /** `fileContentHash` of the content now on disk. */
  hash: z.string(),
});
export type WriteFileResponse = z.infer<typeof WriteFileResponse>;

/** POST /api/worktrees/:id/discard — throws away uncommitted changes to the
 * named paths in the worker's checkout. Tracked files return to HEAD; untracked
 * ones are deleted. A renamed file is discarded by naming both of its paths. */
export const DiscardChangesRequest = z.object({
  paths: z.array(z.string().min(1).max(4096)).min(1).max(200),
  clientRequestId: z.string().min(8).max(64).optional(),
});
export type DiscardChangesRequest = z.infer<typeof DiscardChangesRequest>;
export const DiscardChangesResponse = z.object({
  /** How many of the requested paths the worker touched. */
  discarded: z.number().int().nonnegative(),
});
export type DiscardChangesResponse = z.infer<typeof DiscardChangesResponse>;

/** PATCH /api/worktrees/:id — renames a task. The name is the only user-editable
 * field on a worktree, so this broadcasts the whole row as `worktree.updated`,
 * which every client already folds into its sidebar and task list. */
export const UpdateWorktreeRequest = z.object({
  name: z.string().trim().min(1).max(120),
});
export type UpdateWorktreeRequest = z.infer<typeof UpdateWorktreeRequest>;
export const UpdateWorktreeResponse = z.object({ worktree: Worktree });
export type UpdateWorktreeResponse = z.infer<typeof UpdateWorktreeResponse>;

export const PullRequestCheck = z.object({
  name: z.string(),
  status: z.enum(["queued", "in_progress", "completed"]),
  conclusion: z.string().nullable(),
  url: z.string().url().nullable(),
});
export type PullRequestCheck = z.infer<typeof PullRequestCheck>;

export const PullRequestMergeState = z.enum([
  "checking", "ready", "blocked", "conflicts", "draft", "merged", "closed",
]);
export type PullRequestMergeState = z.infer<typeof PullRequestMergeState>;

/** Live GitHub state for the review card. Webhooks fan this out over realtime;
 * clients also poll as a recovery path for missed or unconfigured events. */
export const PullRequestStatus = z.object({
  number: z.number().int().positive(),
  url: z.string().url(),
  title: z.string(),
  body: z.string().nullable(),
  state: z.string(),
  draft: z.boolean(),
  headSha: z.string(),
  mergeState: PullRequestMergeState,
  checks: z.array(PullRequestCheck),
  checksAvailable: z.boolean(),
  checkedAt: z.number().int().nonnegative(),
});
export type PullRequestStatus = z.infer<typeof PullRequestStatus>;

export const MergePullRequestRequest = z.object({
  /** The exact PR head the user reviewed. GitHub refuses the merge if the
   * branch changed after that review. */
  expectedHeadSha: z.string().min(1).max(64),
  clientRequestId: z.string().min(8).max(64).optional(),
  /** Merge despite Rivets' own readiness gate: checks that are failing,
   * running, unavailable or stale, a pending review, unresolved threads, or
   * local work Rivets has not reconciled. The reviewed head still has to match,
   * and GitHub's own branch protection still has the last word. */
  force: z.boolean().optional(),
});
export type MergePullRequestRequest = z.infer<typeof MergePullRequestRequest>;
export const MergePullRequestResponse = z.object({
  merged: z.boolean(),
  message: z.string(),
  sha: z.string().nullable(),
});
export type MergePullRequestResponse = z.infer<typeof MergePullRequestResponse>;

// -- attachments -------------------------------------------------------------

/** POST /api/orgs/:orgId/attachments — raw file bytes, `content-type` is the
 * media type, `?name=` the original filename and `?width=`/`?height=` the
 * client-measured pixel size when applicable. Org-scoped rather than chat-scoped: files are
 * attached while composing, before the chat they belong to exists. */
export const UploadAttachmentResponse = z.object({ attachment: AttachmentRef });
export type UploadAttachmentResponse = z.infer<typeof UploadAttachmentResponse>;

// -- chats / jobs ------------------------------------------------------------

/** POST /api/worktrees/:id/chats — creates chat + first job. */
export const CreateChatRequest = z.object({
  provider: ProviderSelection,
  prompt: z.string().trim().min(1).max(20_000),
  title: z.string().max(120).optional(),
  /** Files uploaded ahead of this request; only `id` is trusted server-side. */
  attachments: AttachmentRefs.optional(),
  /** Client-generated unique id; replaying the same id returns the original result instead of creating a duplicate. */
  clientRequestId: z.string().min(8).max(64),
});
export type CreateChatRequest = z.infer<typeof CreateChatRequest>;

/** PATCH /api/chats/:id — renames a conversation. Broadcasts `chat.updated`. */
export const UpdateChatRequest = z.object({
  title: z.string().trim().min(1).max(120),
});
export type UpdateChatRequest = z.infer<typeof UpdateChatRequest>;
export const UpdateChatResponse = z.object({ chat: Chat });
export type UpdateChatResponse = z.infer<typeof UpdateChatResponse>;

/** POST /api/chats/:id/handoff — creates a linked conversation on the same
 * worktree and bootstraps it with a bounded, server-built transcript. */
export const HandoffChatRequest = z.object({
  provider: ProviderSelection,
  prompt: z.string().trim().min(1).max(20_000).optional(),
  /** Automatically select another compatible worker and transfer the latest
   * durable file state into a new linked worktree. */
  reroute: z.boolean().optional(),
  clientRequestId: z.string().min(8).max(64),
});
export type HandoffChatRequest = z.infer<typeof HandoffChatRequest>;

export const HandoffChatResponse = z.object({
  worktree: Worktree,
  chat: Chat,
  job: Job,
  sourceChatId: z.string(),
  importedEventCount: z.number().int().nonnegative(),
  transcriptTruncated: z.boolean(),
});
export type HandoffChatResponse = z.infer<typeof HandoffChatResponse>;

export const ChatDetailResponse = z.object({
  chat: Chat,
  jobs: z.array(Job),
});
export type ChatDetailResponse = z.infer<typeof ChatDetailResponse>;

/**
 * GET /api/chats/:id/events. Three mutually exclusive modes
 * (`parseChatEventsQuery`), all answering `ChatEventsPage`:
 *  - `?afterSeq=&limit=` — the next events after a cursor;
 *  - `?tailTurns=N` — the newest N turns (1..50);
 *  - `?beforeSeq=S&turns=N` — N turns before S (exclusive), for scroll-up.
 *
 * Every page elides superseded deltas (`elideSupersededDeltas`). Continue from
 * `coverage`, never from the array length: elided and absent seqs are inside
 * it.
 */
export const ChatEventsPage = z.object({
  events: z.array(ChatEvent),
  /** Newest seq in the chat, whatever this page covers. */
  latestSeq: z.number(),
  /** Oldest seq the chat still has; null for an empty chat. */
  firstSeq: z.number().int().nullable(),
  hasMoreBefore: z.boolean(),
  hasMoreAfter: z.boolean(),
  /** Inclusive seq interval scanned, including elided and absent seqs; null
   * for empty history. A `tailTurns` page does not mark anything before
   * `coverage.fromSeq` as covered. */
  coverage: ChatEventsCoverage.nullable(),
});
export type ChatEventsPage = z.infer<typeof ChatEventsPage>;
/** The same shape under its earlier name; prefer `ChatEventsPage`. */
export const ChatEventsResponse = ChatEventsPage;
export type ChatEventsResponse = ChatEventsPage;

// -- snapshots (K3) ------------------------------------------------------------

/** GET /api/worktrees/:id/snapshot?view=summary answers `SnapshotSummary`. */
export const SNAPSHOT_SUMMARY_VIEW = "summary";

/** Which file list a per-file diff belongs to: `changes` (uncommitted) or
 * `branchChanges` (the task branch against its base). */
export const SnapshotDiffScope = z.enum(["changes", "branch"]);
export type SnapshotDiffScope = z.infer<typeof SnapshotDiffScope>;

/** GET /api/snapshots/:snapshotId/diff?path=&scope= and LocalApi
 * GET /v1/local/worktrees/:id/diff?snapshotId=&path=&scope=. Immutable per
 * `(snapshotId, path, scope)`; a pruned snapshot answers 410
 * `SnapshotGoneResponse` and the client reloads the summary. */
export const SnapshotDiffResponse = z.object({
  snapshotId: z.string(),
  path: z.string(),
  scope: SnapshotDiffScope,
  /** Null for a binary file or one whose diff the budget dropped. */
  diff: z.string().nullable(),
  /** sha-256 hex of `diff`, or `""` when null. Equals the summary's. */
  diffHash: z.string(),
  truncated: z.boolean(),
});
export type SnapshotDiffResponse = z.infer<typeof SnapshotDiffResponse>;

/** GET /api/snapshots/:snapshotId/paths — tracked and untracked paths, loaded
 * lazily for transcript file-link resolution. */
export const SnapshotPathsResponse = z.object({ paths: z.array(z.string()) });
export type SnapshotPathsResponse = z.infer<typeof SnapshotPathsResponse>;

export const SNAPSHOT_GONE_CODE = "snapshot_gone";
/** 410 body for a pruned, unknown or (LocalApi) no-longer-current snapshot. */
export const SnapshotGoneResponse = z.object({
  code: z.literal(SNAPSHOT_GONE_CODE),
  message: z.string().optional(),
});
export type SnapshotGoneResponse = z.infer<typeof SnapshotGoneResponse>;

/** POST /api/chats/:id/messages — user message; steers running job or starts one. */
export const SendMessageRequest = z.object({
  text: z.string().trim().min(1).max(20_000),
  /** Images uploaded ahead of this request; only `id` is trusted server-side. */
  attachments: AttachmentRefs.optional(),
  /** Optional next-turn runtime settings. Active jobs continue with their current settings. */
  provider: ProviderSelection.optional(),
  /** Client-generated unique id; replaying the same id returns the original result instead of creating a duplicate. */
  clientRequestId: z.string().min(8).max(64),
  /** Team messages from this chat to hand the agent along with `text`, which
   * the server reads and frames ahead of it. Refused in assistant chats. */
  teamContext: TeamMessageContext.optional(),
});
export type SendMessageRequest = z.infer<typeof SendMessageRequest>;

/** POST /api/chats/:id/team-messages — a message to the people on the task,
 * not the agent. Needs read access to the task's repository, not the worker:
 * talking runs nothing on anyone's machine. Every mentioned member must be
 * able to read the task (`mention_without_access` otherwise). */
export const SendTeamMessageRequest = z.object({
  text: z.string().trim().min(1).max(TEAM_MESSAGE_MAX_CHARS),
  mentions: TeamMentions.default([]),
  clientRequestId: z.string().min(8).max(64),
});
export type SendTeamMessageRequest = z.infer<typeof SendTeamMessageRequest>;
export const SendTeamMessageResponse = z.object({ ok: z.literal(true), messageId: z.string() });
export type SendTeamMessageResponse = z.infer<typeof SendTeamMessageResponse>;

/** PATCH /api/chats/:id/team-messages/:messageId — author only. */
export const ReviseTeamMessageRequest = z.object({
  text: z.string().trim().min(1).max(TEAM_MESSAGE_MAX_CHARS),
  mentions: TeamMentions.default([]),
});
export type ReviseTeamMessageRequest = z.infer<typeof ReviseTeamMessageRequest>;

// DELETE /api/chats/:id/team-messages/:messageId — author only; no body.

/** 422 code: a mentioned member cannot read the task's repository. */
export const MENTION_WITHOUT_ACCESS_CODE = "mention_without_access";

// -- inbox -------------------------------------------------------------------

/** Most entries one inbox page returns. */
export const INBOX_PAGE_MAX = 100;

/** GET /api/orgs/:orgId/notifications?limit=&before= — the caller's own
 * inbox, newest first, without entries for tasks they can no longer read.
 * `before` is a `createdAt` to page back from. */
export const ListNotificationsResponse = z.object({
  notifications: z.array(InboxNotification),
  /** Across the whole inbox, not just this page. */
  unreadCount: z.number().int().nonnegative(),
  hasMore: z.boolean(),
});
export type ListNotificationsResponse = z.infer<typeof ListNotificationsResponse>;

/** POST /api/orgs/:orgId/notifications/read — exactly one of: these entries,
 * everything about one chat (what opening it does), or the whole inbox. */
export const MarkNotificationsReadRequest = z.union([
  z.object({ ids: z.array(z.string().min(1).max(100)).min(1).max(INBOX_PAGE_MAX) }).strict(),
  z.object({ chatId: z.string().min(1).max(100) }).strict(),
  z.object({ all: z.literal(true) }).strict(),
]);
export type MarkNotificationsReadRequest = z.infer<typeof MarkNotificationsReadRequest>;

export const MarkNotificationsReadResponse = z.object({
  ok: z.literal(true),
  /** The entries this request changed; already-read ones are not repeated. */
  ids: z.array(z.string()),
});
export type MarkNotificationsReadResponse = z.infer<typeof MarkNotificationsReadResponse>;

/**
 * GET/PUT /api/worktrees/:id/follow — whether the caller follows a task, which
 * is what puts its runs, prompts and discussion in their inbox. Creating a
 * task, prompting in it, talking in it or being mentioned in it follows it;
 * unfollowing is remembered, and only following again undoes it. Mentions
 * reach a member whether or not they follow.
 */
export const TaskFollowResponse = z.object({
  following: z.boolean(),
  /** Why, when following. */
  reason: z.enum(["created", "participated", "mentioned", "manual"]).optional(),
});
export type TaskFollowResponse = z.infer<typeof TaskFollowResponse>;

export const UpdateTaskFollowRequest = z.object({ following: z.boolean() }).strict();
export type UpdateTaskFollowRequest = z.infer<typeof UpdateTaskFollowRequest>;

/** POST /api/chats/:id/cancel — cancels exactly `jobId` when it is given, so
 * a Stop can never land on a run that started after it was pressed; a terminal
 * job is a successful no-op. Without `jobId` it cancels whatever is active on
 * the chat (a client that has not yet seen the job's id), and a chat with
 * nothing active is a successful no-op too. */
export const CancelChatRequest = z.object({
  jobId: z.string().min(1).optional(),
});
export type CancelChatRequest = z.infer<typeof CancelChatRequest>;

/** PATCH /api/chats/:id/turns/:turnId/message — rewrites a message that is
 * still queued behind a working agent. Attachments are deliberately not
 * editable: they are already materialized on the worker by the time the turn
 * queues, and swapping them would invalidate the paths in the queued prompt. */
export const ReviseMessageRequest = z.object({
  text: z.string().trim().min(1).max(20_000),
});
export type ReviseMessageRequest = z.infer<typeof ReviseMessageRequest>;

// DELETE /api/chats/:id/turns/:turnId/message — withdraws a message still
// queued behind a working agent; no body.
// POST /api/chats/:id/turns/:turnId/send-now — moves a queued message to the
// front of the queue and interrupts the agent's current turn; no body.

// -- push notifications ------------------------------------------------------

export const PushPlatform = z.enum(["apns-ios", "apns-macos"]);
export type PushPlatform = z.infer<typeof PushPlatform>;

/** POST /api/push/devices — registers or refreshes an APNs device token. */
export const RegisterDeviceRequest = z.object({
  deviceToken: z.string().regex(/^[0-9a-fA-F]{32,200}$/),
  platform: PushPlatform,
  orgId: z.string(),
});
export type RegisterDeviceRequest = z.infer<typeof RegisterDeviceRequest>;

// -- worker-facing REST (bearer worker token) --------------------------------

/** Upper bounds on one snapshot upload. A worktree past them is answered 413
 * `snapshot_too_large` rather than left to exhaust the Worker's CPU parsing
 * and hashing it on every upload. */
export const SNAPSHOT_UPLOAD_MAX_PATHS = 200_000;
export const SNAPSHOT_UPLOAD_MAX_CHANGES = 20_000;
export const SNAPSHOT_UPLOAD_MAX_COMMITS = 2_000;
/** The request body, in bytes. Diff text is budgeted well inside it
 * (`SNAPSHOT_DIFF_BUDGET_BYTES`); the rest is paths and file stats. */
export const SNAPSHOT_UPLOAD_MAX_BYTES = 32 * 1024 * 1024;

/** PUT /api/worker/worktrees/:id/snapshot — full snapshot JSON; stored in R2.
 * An upload identical to the worktree's current snapshot is answered with
 * that snapshot's id and writes nothing. */
export const UploadSnapshotRequest = Snapshot.omit({ id: true, createdAt: true }).extend({
  paths: z.array(z.string()).max(SNAPSHOT_UPLOAD_MAX_PATHS),
  changes: z.array(FileChange).max(SNAPSHOT_UPLOAD_MAX_CHANGES),
  branchChanges: z.array(FileChange).max(SNAPSHOT_UPLOAD_MAX_CHANGES).optional(),
  commits: z.array(GitCommit).max(SNAPSHOT_UPLOAD_MAX_COMMITS),
});
export type UploadSnapshotRequest = z.infer<typeof UploadSnapshotRequest>;

/** PUT /api/worker/worktrees/:id/handoff-backup — records the latest git-native
 * scratch-ref backup a worker pushed for this worktree, so a cross-worker
 * handoff can fetch and reset to it. Both must be full git object ids (sha-1 or
 * sha-256): a half-present/empty pair would otherwise pass the reroute
 * availability guard yet restore nothing. */
const GitObjectId = z.string().regex(/^[0-9a-f]{40}(?:[0-9a-f]{24})?$/, "must be a full git object id");
export const RecordHandoffBackupRequest = z.object({
  wipCommit: GitObjectId,
  baseCommit: GitObjectId,
});
export type RecordHandoffBackupRequest = z.infer<typeof RecordHandoffBackupRequest>;


// -- api tokens --------------------------------------------------------------

/** A long-lived credential for the MCP server and any other headless caller.
 * Only the SHA-256 of the secret is stored; `prefix` is the displayable head of
 * the plaintext so two tokens can be told apart in a list. `kind: "assistant"`
 * rows are minted per assistant session and are never listed or rendered. */
export const ApiToken = z.object({
  id: z.string(),
  orgId: z.string(),
  userId: z.string(),
  kind: z.enum(["personal", "assistant"]),
  name: z.string(),
  prefix: z.string(),
  scopes: z.array(ApiTokenScope),
  createdAt: z.number(),
  lastUsedAt: z.number().nullish(),
  expiresAt: z.number().nullish(),
  revokedAt: z.number().nullish(),
});
export type ApiToken = z.infer<typeof ApiToken>;

/** POST /api/orgs/:orgId/tokens */
export const CreateApiTokenRequest = z.object({
  name: z.string().trim().min(1).max(60),
  scopes: z.array(ApiTokenScope).min(1).max(4).default(DEFAULT_API_TOKEN_SCOPES),
  expiresInDays: z.number().int().min(1).max(365).optional(),
  /** Client-generated unique id; replaying the same id returns the original result instead of creating a duplicate. */
  clientRequestId: z.string().min(8).max(64),
});
export type CreateApiTokenRequest = z.infer<typeof CreateApiTokenRequest>;
export type CreateApiTokenRequestInput = z.input<typeof CreateApiTokenRequest>;

/** `secret` is the only time the plaintext exists outside the caller's hands. */
export const CreateApiTokenResponse = z.object({ token: ApiToken, secret: z.string() });
export type CreateApiTokenResponse = z.infer<typeof CreateApiTokenResponse>;

/** GET /api/orgs/:orgId/tokens — personal tokens only. */
export const ApiTokensResponse = z.object({ tokens: z.array(ApiToken) });
export type ApiTokensResponse = z.infer<typeof ApiTokensResponse>;

// -- channels ----------------------------------------------------------------

/** Conversational surfaces the assistant can be reached from. The database
 * column is deliberately free text, so adding an adapter never rebuilds a
 * table; this enum is only what the current clients render. */
export const ChannelProvider = z.enum(["slack", "teams"]);
export type ChannelProvider = z.infer<typeof ChannelProvider>;

/** One workspace/tenant install, as shown in Integrations settings. Backed by
 * an `integration_connection` row, so `id` is that connection's id. */
export const ChannelInstallation = z.object({
  id: z.string(),
  provider: ChannelProvider,
  /** Provider tenant id (Slack team id, Teams tenant id). */
  externalTeamId: z.string(),
  teamName: z.string().nullish(),
  teamUrl: z.string().url().nullish(),
  /** Display name of the member who installed it. */
  installedBy: z.string(),
  installedAt: z.number(),
  status: z.enum(["active", "revoked", "error"]),
});
export type ChannelInstallation = z.infer<typeof ChannelInstallation>;

/** GET /api/orgs/:orgId/channels */
export const ChannelInstallationsResponse = z.object({
  installations: z.array(ChannelInstallation),
  /** Providers whose OAuth credentials are configured on this deployment. */
  configured: z.array(ChannelProvider),
});
export type ChannelInstallationsResponse = z.infer<typeof ChannelInstallationsResponse>;

/** POST /api/orgs/:orgId/channels/:provider/install — owner-only; the nonce is
 * stored server-side exactly as the Linear install flow does. */
export const StartChannelInstallRequest = z.object({
  /** Native clients come back through a deep link instead of the web app,
   * and must then send the PKCE-style `binding` (see CompleteNativeOAuthRequest). */
  client: z.enum(["native"]).optional(),
  binding: OAuthBindingChallenge.optional(),
});
export type StartChannelInstallRequest = z.infer<typeof StartChannelInstallRequest>;
export const StartChannelInstallResponse = z.object({ url: z.string().url() });
export type StartChannelInstallResponse = z.infer<typeof StartChannelInstallResponse>;

/** Whether the signed-in member's Rivets account is bound to their identity in
 * the channel. Unlinked senders get a one-time link challenge instead of a
 * reply, so the assistant never acts as the wrong person. */
export const ChannelIdentityLink = z.object({
  provider: ChannelProvider,
  linked: z.boolean(),
  externalUserId: z.string().nullish(),
  externalUserName: z.string().nullish(),
  linkedAt: z.number().nullish(),
});
export type ChannelIdentityLink = z.infer<typeof ChannelIdentityLink>;

/** GET /api/orgs/:orgId/channels/:provider/identity */
export const ChannelIdentityLinkResponse = z.object({ link: ChannelIdentityLink });
export type ChannelIdentityLinkResponse = z.infer<typeof ChannelIdentityLinkResponse>;


// -- assistant ---------------------------------------------------------------

/** One member's assistant workspace: a real worktree, marked `source_kind =
 * "assistant-workspace"`, on a branch that is never pushed. (Tasks the
 * assistant *delegates* carry `source_kind = "assistant"` and are ordinary,
 * fully visible work.) Conversations inside it are ordinary chats, so every
 * client reuses its existing chat machinery. */
export const AssistantSession = z.object({
  id: z.string(),
  orgId: z.string(),
  userId: z.string(),
  worktreeId: z.string(),
  workerId: z.string(),
  repoId: z.string(),
  provider: ProviderSelection,
  status: z.enum(["active", "retired"]),
  createdAt: z.number(),
});
export type AssistantSession = z.infer<typeof AssistantSession>;

/** The external thread an assistant conversation is mirrored into, when it did
 * not start in a Rivets client. */
export const AssistantConversationChannel = z.object({
  provider: ChannelProvider,
  /** Provider tenant name, for a "via Slack (Acme)" label. */
  teamName: z.string().nullish(),
  /** Deep link back to the originating thread, when the provider gives one. */
  url: z.string().url().nullish(),
  status: z.enum(["active", "unlinked", "closed"]),
});
export type AssistantConversationChannel = z.infer<typeof AssistantConversationChannel>;

/** An assistant conversation *is* a chat: `chatId` subscribes, replays,
 * steers and cancels through the existing chat endpoints and frames. */
export const AssistantConversation = z.object({
  chatId: z.string(),
  sessionId: z.string(),
  worktreeId: z.string(),
  title: z.string(),
  /** Where the conversation started: `rivets`, or a channel provider id.
   * Free text so a new adapter needs no contract change. */
  origin: z.string().min(1).max(40),
  channel: AssistantConversationChannel.nullish(),
  /** Started from a channel message the owner has not approved yet: the chat
   * is `awaiting_approval` and `GET /api/chats/:id/approval` has the prompt. */
  awaitingApproval: z.boolean(),
  createdAt: z.number(),
  lastActivityAt: z.number().nullish(),
});
export type AssistantConversation = z.infer<typeof AssistantConversation>;

/** GET /api/orgs/:orgId/assistant — `available: false` with a reason is a
 * normal, renderable state (no repo, no capable worker), never an error. */
export const AssistantStateResponse = z.object({
  session: AssistantSession.nullable(),
  conversations: z.array(AssistantConversation),
  available: z.boolean(),
  unavailableReason: z.string().optional(),
});
export type AssistantStateResponse = z.infer<typeof AssistantStateResponse>;

/** POST /api/orgs/:orgId/assistant/conversations. Follow-up messages use the
 * existing POST /api/chats/:id/messages. */
export const StartAssistantConversationRequest = z.object({
  prompt: z.string().trim().min(1).max(20_000),
  title: z.string().max(120).optional(),
  /** Client-generated unique id; replaying the same id returns the original result instead of creating a duplicate. */
  clientRequestId: z.string().min(8).max(64),
});
export type StartAssistantConversationRequest = z.infer<typeof StartAssistantConversationRequest>;

export const StartAssistantConversationResponse = z.object({
  session: AssistantSession,
  conversation: AssistantConversation,
  chat: Chat,
  job: Job,
});
export type StartAssistantConversationResponse = z.infer<typeof StartAssistantConversationResponse>;

/** `code` of an HTTP 504 for an operation the worker may or may not have
 * carried out: it went quiet or disconnected mid-request (P3). Clients say they
 * lost contact and refetch; they never report it as failed. */
export const WORKER_OUTCOME_UNKNOWN_CODE = "worker_outcome_unknown";

/** `code` of an HTTP 409 for an idempotent request whose earlier attempt may
 * have mutated before it failed (P7). Treated like
 * `WORKER_OUTCOME_UNKNOWN_CODE`: refetch, never "failed", never a duplicate. */
export const IDEMPOTENCY_OUTCOME_UNCERTAIN_CODE = "idempotency_outcome_uncertain";

export const ApiError = z.object({
  error: z.string(),
  code: z.string().optional(),
  /** The server's clock (epoch ms), on a `worker_proof_stale` refusal. */
  serverTime: z.number().optional(),
});
export type ApiError = z.infer<typeof ApiError>;

/**
 * The one answer that means a sign-in is over: the request's session is
 * expired, revoked or signed out. Every other 401 — a device proof that
 * failed, a ticket that lapsed — is about that one request, and a client must
 * not sign out over it. Native clients still confirm with a session probe
 * (`GET /api/native/device-proof/session`) before erasing anything.
 */
export const SESSION_EXPIRED_CODE = "session_expired";

/** How long a sign-in lasts without use. Any authenticated request made more
 * than `SESSION_REFRESH_AGE_SECONDS` after the last refresh slides it forward
 * again, so a session in regular use never expires. */
export const SESSION_EXPIRES_IN_SECONDS = 30 * 24 * 60 * 60;
export const SESSION_REFRESH_AGE_SECONDS = 24 * 60 * 60;
